import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Avatar } from './Avatar'
import { useAuthStore } from '../../store/auth'
import { useLogout } from '../../hooks/useAuth'
import { useMyChurches } from '../../hooks/useChurches'
import { LogOut, User, Shield, ChevronRight, Building2 } from 'lucide-react'

interface AvatarMenuProps {
  size?: number
  /** Current church slug — highlighted in the workspace list when inside AdminShell */
  activeChurchSlug?: string
  /** Which direction the popover opens relative to the trigger (default: down) */
  direction?: 'down' | 'up'
  /** Which edge of the trigger the popover aligns to (default: right) */
  align?: 'left' | 'right'
}

export function AvatarMenu({ size = 34, activeChurchSlug, direction = 'down', align = 'right' }: AvatarMenuProps) {
  const { user } = useAuthStore()
  const logout = useLogout()
  const { data: churchesData } = useMyChurches()
  const churches = churchesData?.data ?? []
  const isSuperAdmin = user?.role === 'super_admin'

  const [open, setOpen] = useState(false)
  const [popoverPos, setPopoverPos] = useState<React.CSSProperties>({})
  const containerRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) return
    function handleDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleDown)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleDown)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  function handleToggle() {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect()
      setPopoverPos(
        direction === 'up'
          ? {
              bottom: window.innerHeight - rect.top + 8,
              ...(align === 'left' ? { left: rect.left } : { right: window.innerWidth - rect.right }),
            }
          : {
              top: rect.bottom + 8,
              ...(align === 'left' ? { left: rect.left } : { right: window.innerWidth - rect.right }),
            },
      )
    }
    setOpen((v) => !v)
  }

  function handleLogout() {
    setOpen(false)
    logout.mutate()
  }

  const roleLabel: Record<string, string> = {
    owner: 'Owner',
    coordinator: 'Coordinator',
    viewer: 'Viewer',
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <button
        ref={buttonRef}
        aria-label="Account menu"
        aria-expanded={open}
        onClick={handleToggle}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'inline-flex', borderRadius: '50%' }}
      >
        <Avatar name={user?.displayName ?? '?'} src={user?.avatarUrl} size={size} />
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: 'fixed',
            ...popoverPos,
            width: 220,
            background: 'var(--surface)',
            border: '1px solid var(--line-soft)',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0,0,0,.12)',
            zIndex: 200,
            overflow: 'hidden',
          }}
        >
          {/* Identity header */}
          <div style={{ padding: '12px 14px 10px', borderBottom: '1px solid var(--line-soft)' }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.displayName}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.email}
            </div>
          </div>

          {/* Profile */}
          <div style={{ padding: '6px 6px 0' }}>
            <MenuLink to="/account" icon={<User size={14} strokeWidth={1.8} />} label="My account" onClick={() => setOpen(false)} />
            <MenuLink to="/account?tab=events" icon={<ChevronRight size={14} strokeWidth={1.8} />} label="My events" onClick={() => setOpen(false)} />
          </div>

          {/* Church workspaces */}
          {churches.length > 0 && (
            <div style={{ borderTop: '1px solid var(--line-soft)', padding: '6px 6px 0', marginTop: 4 }}>
              <div style={{ padding: '4px 10px 6px', fontSize: 10.5, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted)' }}>
                Workspaces
              </div>
              {churches.map((c) => {
                const isActive = c.slug === activeChurchSlug
                return (
                  <Link
                    key={c.id}
                    to={`/churches/${c.slug}/dashboard`}
                    role="menuitem"
                    onClick={() => setOpen(false)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 9,
                      padding: '7px 10px',
                      borderRadius: 8,
                      textDecoration: 'none',
                      background: isActive ? 'var(--primary-soft)' : 'transparent',
                      color: isActive ? 'var(--primary)' : 'var(--ink)',
                    }}
                  >
                    <div style={{
                      width: 22,
                      height: 22,
                      borderRadius: 5,
                      background: isActive
                        ? 'linear-gradient(135deg, #3B4CCA 0%, #7C5CD3 100%)'
                        : 'var(--surface-2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      overflow: 'hidden',
                    }}>
                      {c.logoUrl
                        ? <img src={c.logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <Building2 size={12} strokeWidth={1.8} color={isActive ? '#fff' : 'var(--muted)'} />
                      }
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.name}
                      </div>
                      <div style={{ fontSize: 10.5, color: 'var(--muted)', marginTop: 0.5 }}>
                        {roleLabel[c.role] ?? c.role}
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}

          {/* Super admin */}
          {isSuperAdmin && (
            <div style={{ borderTop: '1px solid var(--line-soft)', padding: '6px 6px 0', marginTop: 4 }}>
              <MenuLink to="/admin" icon={<Shield size={14} strokeWidth={1.8} />} label="Super admin" onClick={() => setOpen(false)} />
            </div>
          )}

          {/* Sign out */}
          <div style={{ borderTop: '1px solid var(--line-soft)', padding: '6px 6px 8px', marginTop: 4 }}>
            <button
              role="menuitem"
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                padding: '7px 10px',
                borderRadius: 8,
                border: 'none',
                background: 'none',
                color: 'var(--ink-2)',
                fontSize: 13,
                fontWeight: 500,
                cursor: 'pointer',
                width: '100%',
                textAlign: 'left',
              }}
            >
              <LogOut size={14} strokeWidth={1.8} />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function MenuLink({
  to,
  icon,
  label,
  onClick,
}: {
  to: string
  icon: React.ReactNode
  label: string
  onClick: () => void
}) {
  return (
    <Link
      to={to}
      role="menuitem"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        padding: '7px 10px',
        borderRadius: 8,
        textDecoration: 'none',
        color: 'var(--ink-2)',
        fontSize: 13,
        fontWeight: 500,
      }}
    >
      {icon}
      {label}
    </Link>
  )
}
